import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CalendarDays, Home, Inbox, LayoutGrid, LogOut, Receipt, RefreshCw, Users, Building2, GraduationCap, Sparkles } from "lucide-react";
import { api, connexion, garderLaSession } from "../console/api";
import { espaceApi, messageErreur, CLE_JETON } from "../espace/api";
import { BoutonChoixEspace, CLE_ROLE, ouvrirEspace, useRoles } from "../components/ChoixEspace";
import { Ligne } from "../espace/ui";
import { profsAvecPhoto } from "../lib/photosProfs";
import { Journee } from "./Journee";
import { Messages } from "./ATraiter";
import { Eleves } from "./Eleves";
import { TableauBord } from "./TableauBord";
import { Plus } from "./Plus";
import { ProfsMobile } from "./ProfsMobile";
import { FacturesMobile } from "./FacturesMobile";
import { PrivesMobile } from "./PrivesMobile";
import { useChantiers } from "../lib/useChantiers";

const ONGLETS = [
  { id: "accueil", label: "Accueil", titre: "Tableau de bord", Icone: Home },
  { id: "journee", label: "Journée", titre: "La journée", Icone: CalendarDays },
  { id: "eleves", label: "Élèves", titre: "Élèves", Icone: Users },
  { id: "messages", label: "Messages", titre: "Messages", Icone: Inbox },
  { id: "plus", label: "Plus", titre: "Plus", Icone: LayoutGrid },
];
/* La nouvelle barre (école, 30.10.2026 ; version de test d'abord, interrupteur app_ecole) :
   Profs et Factures deviennent des onglets, Messages passe dans « Plus ». */
const ONGLETS_NOUVEAUX = [
  { id: "accueil", label: "Accueil", titre: "Tableau de bord", Icone: Home },
  { id: "journee", label: "Journée", titre: "La journée", Icone: CalendarDays },
  { id: "eleves", label: "Élèves", titre: "Élèves", Icone: Users },
  { id: "profs", label: "Profs", titre: "Profs", Icone: Briefcase },
  { id: "factures", label: "Factures", titre: "Factures", Icone: Receipt },
  { id: "plus", label: "Plus", titre: "Plus", Icone: LayoutGrid },
];
/* « Privés » (Jen, 30.10.2026) : les cours privés, là où leur réservation est ouverte. */
const ONGLET_PRIVES = { id: "prives", label: "Privés", titre: "Cours privés", Icone: Sparkles };

/* Pas encore de session école : on la demande avec la connexion de l'app (Google ou lien
   e-mail), sinon avec le mot de passe de la console. */
const Connexion = ({ onConnecte }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [erreur, setErreur] = useState("");
  const parConsole = async (e) => {
    e.preventDefault();
    setErreur("");
    const ok = await connexion(form);
    if (ok) onConnecte(); else setErreur("E-mail ou mot de passe incorrect.");
  };
  return (
    <div className="min-h-[100dvh] px-5 py-10 flex flex-col justify-center" style={{ background: "var(--bg-soft, #EEF6F8)" }}>
      <div className="max-w-lg w-full mx-auto space-y-5">
        <div className="text-center">
          <span className="w-14 h-14 rounded-2xl grid place-items-center mx-auto text-white" style={{ background: "#0F1B2D" }}>
            <Building2 size={26} />
          </span>
          <h1 className="font-display text-[26px] mt-4">Mode école</h1>
          <p className="text-sm text-[var(--muted)] mt-1">La console de 1000 ARTS, au format téléphone.</p>
        </div>
        <button onClick={() => { try { localStorage.setItem(CLE_ROLE, "ecole"); } catch { /* rien */ } navigate("/espace"); }}
          className="btn-primary w-full justify-center" data-testid="ecole-connexion-app">
          Me connecter avec mon compte de l'app
        </button>
        <p className="text-[12.5px] text-[var(--muted)] text-center">Google ou lien par e-mail, avec l'adresse que l'école a autorisée.</p>
        <form onSubmit={parConsole} className="card-flat space-y-3 bg-white">
          <div className="text-[13px] font-semibold text-[var(--muted)]">Ou avec le mot de passe de la console</div>
          <input className="input-field w-full" type="email" placeholder="E-mail de la console" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="username" />
          <input className="input-field w-full" type="password" placeholder="Mot de passe" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" />
          {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
          <button className="btn-ghost w-full justify-center" disabled={!form.email || !form.password}>Se connecter</button>
        </form>
      </div>
    </div>
  );
};

export default function EcoleApp() {
  const navigate = useNavigate();
  const roles = useRoles();
  const [etat, setEtat] = useState("chargement");
  const [erreur, setErreur] = useState("");
  const [resume, setResume] = useState(null);
  const [onglet, setOngletBrut] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("onglet") || sessionStorage.getItem("ecole_onglet") || "accueil"; } catch { return "accueil"; }
  });
  // `cible` : ce qu'il faut ouvrir dans l'onglet (une fiche élève, une séance, un écran
  // de « Plus ») quand on y arrive depuis le tableau de bord ou une alerte.
  // Une notification ouvre directement le bon écran (école, 01.11.2026 : « quand elle clique
  // dessus, ça l'amène à l'accueil ») : ?onglet=plus&ecran=alertes&groupe=nouvelles_inscriptions.
  const [cible, setCible] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("ecran") ? { ecran: p.get("ecran"), groupe: p.get("groupe") || "", n: Date.now() } : null;
    } catch { return null; }
  });
  const chantiers = useChantiers();
  const nouvelleBarre = !!chantiers.app_ecole;
  // L'adresse de la notification ne sert qu'une fois : un rechargement ne rouvre pas l'écran.
  useEffect(() => {
    try { if (window.location.search) window.history.replaceState(null, "", window.location.pathname); } catch { /* rien */ }
  }, []);
  // Les messages vivent dans « Plus » avec la nouvelle barre.
  useEffect(() => {
    if (nouvelleBarre && onglet === "messages") { setCible({ ecran: "messages", n: Date.now() }); setOngletBrut("plus"); }
  }, [nouvelleBarre, onglet]);
  const allerBrut = (o, quoi = null) => { setCible(quoi ? { ...quoi, n: Date.now() } : null); setOnglet(o); };
  // Avec la nouvelle barre, les liens vers les anciens emplacements mènent au bon onglet.
  const aller = (o, quoi = null) => {
    if (!nouvelleBarre) return allerBrut(o, quoi);
    if (o === "messages") return allerBrut("plus", { ecran: "messages" });
    if (o === "plus" && quoi?.ecran === "profs") return allerBrut("profs", quoi);
    if (o === "plus" && quoi?.ecran === "argent") return allerBrut("factures", { vue: "impayes" });
    // Une alerte de facturation (« 2::famille@… », « 2026-2027::1::famille@… ») ouvre la facture.
    if (o === "eleves" && quoi?.facture) {
      const [, , semestre, cle] = /^(?:(\d{4}-\d{4})::)?(?:([12])::)?(.+)$/.exec(quoi.facture) || [];
      return allerBrut("factures", { facture: cle || quoi.q, semestre: Number(semestre) || 0 });
    }
    return allerBrut(o, quoi);
  };
  const quitter = async () => {
    if (!window.confirm("Se déconnecter de l'application ?")) return;
    try { await espaceApi.post("/deconnexion"); } catch { /* déjà déconnecté */ }
    ["admin_token", CLE_JETON, CLE_ROLE, "prof_session"].forEach((k) => { try { localStorage.removeItem(k); } catch { /* rien */ } });
    navigate("/espace");
  };
  const setOnglet = (o) => {
    setOngletBrut(o);
    try { sessionStorage.setItem("ecole_onglet", o); } catch { /* rien */ }
    document.getElementById("ecole-defilement")?.scrollTo({ top: 0 });
  };

  // Qui est connecté (et ses propres cours) : l'accueil dit « Bonjour Noémie ».
  const [moi, setMoi] = useState(null);
  useEffect(() => { if (etat === "ok") api("/mobile/moi").then(setMoi).catch(() => {}); }, [etat]);
  const rafraichir = useCallback(() => api("/mobile/resume").then(setResume).catch(() => {}), []);

  // Une session console valide ? Sinon on en demande une avec la session de l'app.
  // Une session expirée déclenche « console-session-expiree » à chaque appel refusé :
  // sans ce verrou, la reconnexion se relançait elle-même en boucle.
  const enCours = useRef(false);
  const ouvrir = useCallback(async () => {
    if (enCours.current) return;
    enCours.current = true;
    try {
      await ouvrirSansVerrou();
    } finally {
      enCours.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ouvrirSansVerrou = async () => {
    try {
      setResume(await api("/mobile/resume"));
      setEtat("ok");
      return;
    } catch { /* pas de session console */ }
    let jeton = "";
    try { jeton = localStorage.getItem(CLE_JETON) || ""; } catch { /* rien */ }
    if (jeton) {
      try {
        const { data } = await espaceApi.post("/role/ecole");
        localStorage.setItem("admin_token", data.jeton);
        setResume(await api("/mobile/resume"));
        setEtat("ok");
        return;
      } catch (e) {
        setErreur(messageErreur(e));
      }
    }
    setEtat("connexion");
  };

  useEffect(() => {
    document.body.classList.add("espace-app");
    ouvrir();
    const arreter = garderLaSession?.();
    const expiree = () => ouvrir();
    window.addEventListener("console-session-expiree", expiree);
    return () => {
      document.body.classList.remove("espace-app");
      window.removeEventListener("console-session-expiree", expiree);
      if (typeof arreter === "function") arreter();
    };
  }, [ouvrir]);

  if (etat === "chargement") {
    return <div className="min-h-[100dvh] grid place-items-center"><RefreshCw size={22} className="animate-spin" style={{ color: "var(--primary)" }} /></div>;
  }
  if (etat === "connexion") {
    return (
      <>
        {erreur && <div className="fixed top-3 inset-x-3 z-50 text-sm rounded-2xl px-4 py-3" style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>}
        <Connexion onConnecte={ouvrir} />
      </>
    );
  }

  const base = nouvelleBarre ? ONGLETS_NOUVEAUX : ONGLETS;
  const onglets = chantiers.cours_prives ? [...base.slice(0, -1), ONGLET_PRIVES, base[base.length - 1]] : base;
  const titre = onglets.find((o) => o.id === onglet)?.titre;
  const photo = moi?.prenom ? profsAvecPhoto(moi.prenom)[0]?.image : null;
  const badges = nouvelleBarre
    ? { profs: resume?.profs_sans_remplacant, factures: resume?.impayes, plus: resume?.messages }
    : { messages: resume?.messages, plus: (resume?.impayes || 0) + (resume?.profs_sans_remplacant || 0) };
  const autresEspaces = (
    <>
      {roles?.prof && <Ligne Icone={GraduationCap} couleur="#9333EA" titre="Mon espace prof" sousTitre="Mes cours, mes listes, mes heures"
        onClick={() => ouvrirEspace("prof", navigate)} testId="ecole-vers-prof" />}
      {roles?.famille && <Ligne Icone={Home} couleur="#00C6CE" titre="Mon espace famille" sousTitre="Les cours et factures de mes enfants"
        onClick={() => ouvrirEspace("famille", navigate)} testId="ecole-vers-famille" />}
    </>
  );
  const ecrans = {
    accueil: <TableauBord resume={resume} aller={aller} moi={moi} />,
    journee: <Journee cible={cible} aller={aller} />,
    eleves: <Eleves cible={cible} />,
    messages: <Messages onCompte={rafraichir} />,
    plus: <Plus cible={cible} resume={resume} aller={aller} onCompte={rafraichir} onQuitter={quitter} choixEspace={autresEspaces} nouvelleBarre={nouvelleBarre} />,
    profs: <ProfsMobile cible={cible} aller={aller} onCompte={rafraichir} />,
    factures: <FacturesMobile cible={cible} onCompte={rafraichir} />,
    prives: <PrivesMobile />,
  };

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden" style={{ background: "var(--bg-soft, #EEF6F8)" }} data-testid="ecole-app">
      <header className="sticky top-0 z-30 border-b"
        style={{ background: "rgba(255,255,255,0.86)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          borderColor: "var(--border)", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-lg mx-auto px-5 py-3 flex items-center gap-3">
          {photo ? <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" style={{ boxShadow: "0 0 0 2px #fff, 0 0 0 3px var(--border)" }} />
            : (
              <span className="w-10 h-10 rounded-full grid place-items-center shrink-0 text-white" style={{ background: "#0F1B2D" }}>
                {moi?.prenom ? <span className="font-semibold">{moi.prenom[0]}</span> : <Building2 size={17} />}
              </span>
            )}
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold leading-tight truncate">{onglet === "accueil" && moi?.prenom ? moi.nom || moi.prenom : titre}</div>
            <div className="text-[11.5px] text-[var(--muted)] leading-tight flex items-center gap-1"><Building2 size={11} /> Mode école{onglet === "accueil" ? "" : ` · ${moi?.prenom || "1000 ARTS"}`}</div>
          </div>
          <BoutonChoixEspace actuel="ecole" roles={roles} />
          <button onClick={quitter} aria-label="Se déconnecter" data-testid="ecole-deconnexion-entete"
            className="w-10 h-10 grid place-items-center rounded-full shrink-0" style={{ background: "var(--primary-tint)", color: "var(--muted)" }}>
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Sans `-webkit-overflow-scrolling: touch` (01.11.2026) : sur iPhone, cette propriété
          enferme tout ce que l'écran contient dans sa propre pile d'affichage, et une fenêtre
          posée au-dessus de tout (z 50) passait SOUS la barre d'onglets (z 40) — « Créer les
          4 factures à générer ? » sans ses boutons. Le défilement à l'inertie est celui de
          Safari depuis iOS 13. Les feuilles du bas passent en plus par un portail (Feuille.jsx). */}
      <main id="ecole-defilement" className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div className="max-w-lg mx-auto px-5 py-5" style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}>
          {ecrans[onglets.some((o) => o.id === onglet) ? onglet : "accueil"]}
        </div>
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 border-t"
        style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          borderColor: "var(--border)", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)", paddingTop: "2px" }}
        data-testid="ecole-nav">
        <div className="max-w-lg mx-auto grid" style={{ gridTemplateColumns: `repeat(${onglets.length}, minmax(0, 1fr))` }}>
          {onglets.map(({ id, label, Icone }) => {
            const ouvert = onglet === id;
            const n = badges[id];
            return (
              <button key={id} data-testid={`ecole-nav-${id}`}
                // Toucher l'onglet déjà ouvert ramène à son premier écran, comme sur iPhone.
                onClick={() => (id === onglet ? aller(id, { racine: true }) : aller(id))}
                className="relative flex flex-col items-center justify-center gap-1.5 py-3 min-h-[64px]"
                style={{ color: ouvert ? "var(--primary)" : "#475569" }} aria-current={ouvert ? "page" : undefined}>
                <span className="relative">
                  <Icone size={24} strokeWidth={ouvert ? 2.4 : 2} />
                  {n > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-bold grid place-items-center text-white"
                      style={{ background: "#DC2626" }}>{n > 99 ? "99+" : n}</span>
                  )}
                </span>
                <span className="text-[12px] font-semibold leading-none">{label}</span>
                {ouvert && <span className="absolute top-0 w-8 h-[2.5px] rounded-full" style={{ background: "var(--primary)" }} />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
